import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Search, Users, MessageSquare, Loader2,
  Plus, Settings
} from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';

import { ChatRoom } from '@/stores/useChatScreen';

interface ChatListProps {
  rooms: ChatRoom[];
  activeRoomId?: string;
  isLoading: boolean;
  onSelectRoom: (room: ChatRoom) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onCreateGroup?: () => void;
  currentUserId?: string;
}

const ChatList: React.FC<ChatListProps> = ({
  rooms,
  activeRoomId,
  isLoading,
  onSelectRoom,
  searchQuery,
  onSearchChange,
  onCreateGroup,
  currentUserId
}) => {
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'unread' | 'groups' | 'business'>('all');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter rooms based on search and filter type
  const filteredRooms = rooms.filter(room => {
    const matchesSearch = room.room_name.toLowerCase().includes(searchQuery.toLowerCase());

    switch (selectedFilter) {
      case 'unread':
        return matchesSearch && room.unread_count > 0;
      case 'groups':
        return matchesSearch && room.is_group;
      case 'business':
        return matchesSearch && !!room.business_id;
      default:
        return matchesSearch;
    }
  });

  const formatRoomTime = useCallback((dateString?: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

      if (diffInDays === 0) {
        return format(date, 'HH:mm');
      } else if (diffInDays === 1) {
        return 'Yesterday';
      } else if (diffInDays < 7) {
        return format(date, 'EEE');
      } else {
        return format(date, 'dd/MM/yy');
      }
    } catch {
      return '';
    }
  }, []);

  const getTotalUnreadCount = useCallback(() => {
    return rooms.reduce((sum, room) => sum + (room.unread_count || 0), 0);
  }, [rooms]);

  return (
    <div className="flex flex-col h-full bg-light-card dark:bg-dark-card">
      {/* Header */}
      <div className="p-4 border-b border-light-border dark:border-dark-border shrink-0">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold text-light-text dark:text-dark-text">Messages</h2>
          <button
            onClick={onCreateGroup}
            className="p-2 rounded-lg hover:bg-light-border dark:hover:bg-dark-border transition text-light-subtle dark:text-dark-subtle hover:text-light-text dark:hover:text-dark-text"
            title="New group"
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-light-subtle dark:text-dark-subtle pointer-events-none" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search messages..."
            className="w-full bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-xl pl-10 pr-4 py-2.5 text-light-text dark:text-dark-text placeholder-light-subtle dark:placeholder-dark-subtle focus:outline-none focus:ring-2 focus:ring-primary/50 dark:focus:ring-golden/50 focus:border-transparent transition"
          />
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1.5 -mx-1 px-1">
          {['all', 'unread', 'groups', 'business'].map(filter => (
            <button
              key={filter}
              onClick={() => setSelectedFilter(filter as any)}
              className={`px-3 py-1.5 rounded-lg text-xs whitespace-nowrap font-medium transition ${
                selectedFilter === filter
                  ? 'bg-primary dark:bg-golden text-white'
                  : 'bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text hover:bg-light-border dark:hover:bg-dark-border'
              }`}
            >
              {filter === 'all'
                ? 'All'
                : filter === 'unread'
                  ? `Unread (${rooms.filter(r => r.unread_count > 0).length})`
                  : filter === 'groups'
                    ? 'Groups'
                    : 'Business'}
            </button>
          ))}
        </div>
      </div>

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary dark:text-golden" />
          </div>
        ) : filteredRooms.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <MessageSquare className="h-12 w-12 text-light-subtle dark:text-dark-subtle mb-3 opacity-50" />
            <p className="text-light-text dark:text-dark-text font-medium mb-1">
              {searchQuery ? 'No conversations found' : 'No conversations yet'}
            </p>
            <p className="text-sm text-light-subtle dark:text-dark-subtle">
              {searchQuery
                ? 'Try a different search term'
                : 'Start a new conversation to see it here'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-light-border dark:divide-dark-border">
            {filteredRooms.map((room) => (
              <button
                key={room.room_id}
                onClick={() => onSelectRoom(room)}
                className={`w-full text-left p-4 transition-all hover:bg-light-bg dark:hover:bg-dark-bg ${
                  activeRoomId === room.room_id
                    ? 'bg-primary/5 dark:bg-golden/5 border-l-2 border-primary dark:border-golden'
                    : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-golden/20 dark:from-primary/30 dark:to-golden/30 flex items-center justify-center overflow-hidden text-sm font-semibold text-primary dark:text-golden">
                      {room.room_avatar_url ? (
                        <img src={room.room_avatar_url} alt={room.room_name} className="w-full h-full object-cover" />
                      ) : (
                        room.room_name.charAt(0).toUpperCase()
                      )}
                    </div>

                    {/* Unread Badge */}
                    {room.unread_count > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 h-5 min-w-[20px] rounded-full bg-danger/10 dark:bg-danger-dark/100 text-danger dark:text-white text-xs font-semibold flex items-center justify-center px-1.5">
                        {room.unread_count > 99 ? '99+' : room.unread_count}
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-light-text dark:text-dark-text truncate flex items-center gap-1.5">
                          {room.room_name}
                          {room.is_group && (
                            <Users className="h-3.5 w-3.5 text-light-subtle dark:text-dark-subtle flex-shrink-0" />
                          )}
                          {room.business_id && (
                            <span className="text-xs px-1.5 py-0.25 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 rounded flex-shrink-0">
                              Business
                            </span>
                          )}
                        </h4>
                      </div>
                      <span className="text-xs text-light-subtle dark:text-dark-subtle flex-shrink-0">
                        {formatRoomTime(room.last_message_created_at)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <p className={`text-sm truncate max-w-[90%] ${
                        room.unread_count > 0
                          ? 'text-light-text dark:text-dark-text font-medium'
                          : 'text-light-subtle dark:text-dark-subtle'
                      }`}>
                        {room.last_message_content || 'No messages yet'}
                      </p>

                      {room.is_new_connection && (
                        <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-300 px-1.5 py-0.5 rounded-full flex-shrink-0 ml-1">
                          New
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatList;
